import React, { AriaAttributes } from "react";
import { IconType } from "react-icons/lib";
import styled from "styled-components";
import { space, SpaceProps } from "styled-system";
import { darken, lighten } from "polished";
import { accessibleShade } from "../accessibleColor";

const getColor = (props: any, defaultColor?: string) => {
  return (
    props.theme.colors[props.color] ||
    props.color ||
    defaultColor ||
    props.theme.colors.grey3
  );
};

const ButtonElem = styled.button<SpaceProps & { color?: string }>`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  vertical-align: middle;
  padding: 1px;
  background: none;
  border: 0;
  cursor: pointer;
  transition: transform 0.1s ease;

  color: ${(props) => lighten(0.2, getColor(props))};

  &:hover {
    color: ${(props) => getColor(props, props.theme.colors.body)};
  }

  &:disabled {
    color: ${(props) => lighten(0.5, getColor(props))};
    cursor: default;
  }

  &:active:not(:disabled) {
    transform: scale(0.9);
  }

  &:focus-visible {
    outline: 2px solid ${(props) => props.theme.colors.grey0};
    outline-offset: 2px;
  }

  ${space};
`;

ButtonElem.defaultProps = {
  type: "button",
};

/**
 * The filled circle uses the same fill as a primary Button: the colour,
 * deepened just enough for the white icon to read, darkening on hover and
 * press.
 */
const fillOf = (props: any) => accessibleShade(getColor(props));

const FilledButtonElem = styled(ButtonElem)`
  background-color: ${fillOf};
  color: white;
  border-radius: 50%;
  width: 32px;
  height: 32px;

  &:hover {
    background-color: ${(props) => darken(0.06, fillOf(props))};
    color: white;
  }

  &:active:not(:disabled) {
    background-color: ${(props) => darken(0.12, fillOf(props))};
  }
`;

export type IconButtonProps = React.ComponentPropsWithoutRef<"button"> &
  SpaceProps & {
    color?: string;
    icon: IconType;
    buttonStyle?: "filled" | "normal";
  };

export const IconButton = ({
  icon: Icon,
  buttonStyle = "normal",
  children,
  ...rest
}: IconButtonProps) => {
  const Elem = buttonStyle === "filled" ? FilledButtonElem : ButtonElem;

  return (
    <Elem {...rest}>
      <Icon />
    </Elem>
  );
};
